import { useCallback } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { getOptimizelyExperimentVariation, getOptimizelyFeatureFlagVariation, isFeatureFlagExperiment } from 'lib/optimizely/optimizelyUtils'
import type { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { triggerAnalytics, hasAnalyticsTriggered } from './deferredAnalyticsMap'

type OptimizelyId = OptimizelyExperiments | OptimizelyFeatureFlags

interface DeferredAnalyticsResult<T> {
  readonly variation: T
  readonly analyticsTriggered: boolean
  readonly triggerAnalytics: () => void
}

/**
 * @deprecated Prefer `useExperiment(..., { canActivate: false })` with
 * `triggerActivation()` for experiments, or `useFeatureFlag(...)` for feature
 * flags in React components.
 *
 * This legacy hook exists for backwards compatibility while experiment
 * consumers migrate to `useExperiment(...)`.
 *
 * Optimizely experiment/feature-flag with deferred analytics activation.
 *
 * Returns the Optimizely decision immediately but only
 * triggers analytics/activation when explicitly triggered via triggerAnalytics() call.
 *
 * Behavior:
 * - variation: the Optimizely decision (string | undefined for experiments, boolean for flags)
 * - analyticsTriggered: whether analytics have been triggered for this flag
 * - triggerAnalytics(): triggers analytics activation for this flag
 *
 * Typical usage:
 *   const { variation, triggerAnalytics } = useDeferredAnalyticsOptimizely(OptimizelyExperiments.foo)
 *   const enabled = getOptimizelyEnabledDecision(variation)
 *   const onClick = () => { triggerAnalytics(); if (enabled) doThing() }
 *
 * Notes:
 * - Flag decision is available immediately (evaluated in SSR)
 * - Analytics activation is deferred until triggerAnalytics() is called
 * - Supports both experiments and feature flags
 *
 * @param id Optimizely experiment or feature flag id (rule key)
 * @returns { variation, analyticsTriggered, triggerAnalytics }
 */
export function useDeferredAnalyticsOptimizely(id: OptimizelyExperiments): DeferredAnalyticsResult<string | undefined>
export function useDeferredAnalyticsOptimizely(id: OptimizelyFeatureFlags): DeferredAnalyticsResult<boolean>
export function useDeferredAnalyticsOptimizely(id: OptimizelyId): DeferredAnalyticsResult<string | boolean | undefined>
export function useDeferredAnalyticsOptimizely(id: OptimizelyId) {
  const analyticsTriggered = hasAnalyticsTriggered(id)

  const variation = useAppSelector(state =>
    isFeatureFlagExperiment(id)
      ? getOptimizelyFeatureFlagVariation(state, id)
      : getOptimizelyExperimentVariation(state, id),
  )

  // Only trigger analytics when explicitly triggered
  useOptimizelyExperiment(id, analyticsTriggered)

  const memoisedTriggerAnalytics = useCallback(() => {
    triggerAnalytics(id, true)
  }, [id])

  return {
    variation,
    analyticsTriggered,
    triggerAnalytics: memoisedTriggerAnalytics,
  } as const
}
